import React, { useCallback, useEffect, useMemo, useState } from 'react';
import _get from 'lodash/get';
import _trim from 'lodash/trim';
import _toLower from 'lodash/toLower';
import _isArray from 'lodash/isArray';
import _isEmpty from 'lodash/isEmpty';
import styles from './index.module.scss';

import { If } from 'utils';
import { useDebounce, useFetch } from 'utils/hooks';
import { PageErrorUi, UiKit } from 'components';
import {
  Table,
  TableHead,
  TableRow,
  TableRows,
  TableSearch,
  TableZeroState
} from 'components/Tables';
import Loader from './Loader';
import { useProductReturnWindowConfig } from '../../context';
import { ReactComponent as ProductIcon } from 'assets/ss-icons/zero-state-icons/product.svg';

const HEADERS = [
  { title: 'Product Type', lg: 9 },
  { title: 'Total Products', lg: 3 }
];

export default function ProductTypes() {
  const {
    mode,
    initialData,
    onChange,
    onSetInitialProductTypes
  } = useProductReturnWindowConfig();

  const [checked, setChecked] = useState({});
  const [searchValue, setSearchValue] = useState('');
  const debouncedSearchValue = useDebounce(searchValue);
  const searchQuery = _trim(searchValue ? debouncedSearchValue : searchValue);
  const hasInitializedRef = React.useRef(false);

  const { data, isError, isLoading, fetchData } = useFetch('productTypes', {
    loadOnMount: true,
    initialData: [],
    onTransform: data => (_isArray(data) ? data : [])
  });

  useEffect(() => {
    if (
      mode === 'edit' &&
      !_isEmpty(initialData.product_types) &&
      !_isEmpty(data) &&
      !hasInitializedRef.current
    ) {
      hasInitializedRef.current = true;
      const checkedMap = initialData.product_types.reduce((acc, typeId) => {
        const productType = data.find(pt => pt.id === typeId);
        if (productType) {
          acc[typeId] = productType;
        }
        return acc;
      }, {});
      setChecked(checkedMap);

      // Also update parent with full product type objects for initial state
      const productTypeObjects = Object.values(checkedMap);
      if (productTypeObjects.length > 0) {
        onSetInitialProductTypes(productTypeObjects);
      }
    }
  }, [mode, initialData.product_types, data, onSetInitialProductTypes]);

  const onCheckChange = useCallback(
    (productType, isChecked) => {
      setChecked(prev => {
        const updated = { ...prev };
        if (isChecked) {
          updated[productType.id] = productType;
        } else {
          delete updated[productType.id];
        }

        // Update parent form data
        const productTypeIds = Object.keys(updated);
        onChange({ product_types: productTypeIds });

        return updated;
      });
    },
    [onChange]
  );

  const filteredProductTypes = useMemo(() => {
    if (!searchQuery) return data;
    return data.filter(productType => {
      const title = _toLower(_get(productType, 'title', ''));
      return title.includes(_toLower(searchQuery));
    });
  }, [data, searchQuery]);

  return (
    <PageErrorUi
      isError={isError}
      onReload={fetchData}
      msg="Unable to load the product types at the moment."
    >
      <Loader isLoading={isLoading}>
        <UiKit.Flex direction="column">
          <UiKit.Text size="lg" weight="bold">
            Select the Product Types you want to include
          </UiKit.Text>
          <div className={styles.searchContainer}>
            <TableSearch
              value={searchValue}
              disabled={isLoading}
              onChange={setSearchValue}
              onCancel={() => setSearchValue('')}
              placeholder="Search a product type"
            />
          </div>
          <Table className={styles.table}>
            <div className={styles.header}>
              <TableHead headings={HEADERS} className={styles.head} />
            </div>
            <TableRows className={styles.body}>
              <If
                test={!_isEmpty(filteredProductTypes)}
                otherwise={
                  <TableZeroState
                    searchQuery={searchQuery}
                    icon={<ProductIcon />}
                  >
                    There is no product type
                  </TableZeroState>
                }
              >
                {filteredProductTypes.map(productType => {
                  const id = _get(productType, 'id');
                  const title = _get(productType, 'title', '');
                  const count = _get(productType, 'products_count', 0);
                  const isChecked = !!checked[id];

                  return (
                    <TableRow
                      key={id}
                      values={[
                        {
                          lg: 9,
                          CustomComponent: (
                            <UiKit.Checkbox
                              label={title}
                              checked={isChecked}
                              onChange={value =>
                                onCheckChange(productType, value)
                              }
                            />
                          )
                        },
                        {
                          lg: 3,
                          title: count
                        }
                      ]}
                    />
                  );
                })}
              </If>
            </TableRows>
          </Table>
        </UiKit.Flex>
      </Loader>
    </PageErrorUi>
  );
}
